import delve from 'dlv';
import contentMap from './contentMap';
import { entityTypeToApiType } from 'helpers/mapEntityTypes';
import { IIterator } from 'types/shared';
import { IDownloadEntityData } from 'components/data-containers/KPIs/types';
import { IEntity } from 'types/shared';

/**
 * Map data from an array with objects and arrays to one array with mapped values.
 * It merges keys together so we end up with an array of values for each data key.
 *
 * @example
 *
 * Go from:
 * ```
 * const response = [
 *   {
 *     entity: { id: 'USAT21700437', type: 'track' },
 *     data: {
 *       count_this_week_to_date: 430941,
 *       wow_in_progress: 0.17,
 *     },
 *   },
 *   {
 *     entity: { id: 'GBAHS1600463', type: 'track' },
 *     data: {
 *       count_this_week_to_date: 383511,
 *       wow_in_progress: 0.14,
 *     },
 *   },
 * ];
 * ```
 *
 * To:
 * ```
 * const data = {
 *   count_this_week_to_date: {
 *     label: 'This week to date',
 *     value: [
 *       {
 *         value: 430941,
 *         type: 'number'
 *       },
 *       {
 *         value: 383511,
 *         type: 'number,
 *       },
 *     ],
 *   },
 *   wow_in_progress: {
 *     label: 'Week over week to date',
 *     value: [
 *       {
 *         value: 0.17,
 *         type: 'trend',
 *       },
 *       {
 *         value: 0.14,
 *         type: 'trend',
 *       }
 *     ],
 *   }
 * }
 * ```
 */

interface IEntityItem {
  entity: IEntity;
  data?: IIterator;
  countryId?: string;
  track_data?: IDownloadEntityData;
  album_data?: IDownloadEntityData;
}

type IMapKPIEntity = IEntityItem & IIterator;

interface IMapKPIDataProps {
  data: IMapKPIEntity[];
  split?: any;
  compareMode?: string;
  compareArray?: any[];
}

interface IMappedData {
  [key: string]: {
    label: string;
    value: {
      value: number | null;
      type: string;
    }[];
  };
}

const mapKPIData = ({
  data,
  split = undefined,
  compareMode,
  compareArray = [],
}: IMapKPIDataProps): IMappedData | null => {
  if (data) {
    let result = {};
    const mapKey = split ? `${entityTypeToApiType(split)}_data` : 'data';

    data
      /** Filter the (old) data based on the latest compareState */
      .filter((item: IEntityItem): boolean => {
        if (compareMode === 'entity') {
          return compareArray.includes(item.entity.id);
        } else if (compareMode === 'country') {
          return compareArray.includes(item.countryId);
        }

        return true;
      })
      .sort((objA: IEntityItem, objB: IEntityItem): number => {
        if (compareMode === 'entity') {
          /** Sort the compare items based on the entityIds */
          return (
            compareArray.indexOf(objA?.entity?.id) -
            compareArray.indexOf(objB?.entity?.id)
          );
        }

        if (compareMode === 'country') {
          /** Sort the compare items based on the countryIds */
          return (
            compareArray.indexOf(objA?.countryId as string) -
            compareArray.indexOf(objB?.countryId as string)
          );
        }

        return 1;
      })
      .forEach((listItem: IEntityItem & IIterator): void => {
        Object.keys(listItem[mapKey]).forEach((key) => {
          // Check if we have an existing value key in the result, use that array or create a new one
          const existingValue = delve(result, `${key}.value`, []);
          if (!(contentMap as IIterator)[key]) return;
          const newValue = {
            value: listItem[mapKey][key],
            type: (contentMap as IIterator)[key].type,
          };

          result = {
            ...result,
            [key]: {
              // Map information based on the key used
              label: (contentMap as IIterator)[key].label,
              // Explode the existingValue and add the new value to the array
              value: [...existingValue, newValue],
            },
          };
        });
      });

    // Return an object with mapped data
    return result;
  }
  return null;
};

export default mapKPIData;
